import type { OfferId } from "@pax/api-contract/payments/v1alpha1";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, reachable } from "@/lib/api";
import { cachedCredits } from "./queries";
import {
  CHECKOUT_CLOSE_PATH,
  markCheckoutStarted,
  openStripeTab,
  sendToStripe,
} from "./resume-after-checkout";

/** Opens Stripe Checkout in a tab created during the click, then watches for credits on this page. */
export function useStartCheckout({ onStarted }: { onStarted?: () => void } = {}) {
  const queryClient = useQueryClient();
  const checkout = useMutation({
    mutationFn: ({
      offerId,
      returnPath,
    }: {
      offerId: OfferId;
      returnPath: string;
      tab: Window | null;
    }) => reachable(api.payments.checkoutSessions.create({ body: { offerId, returnPath } })),
    onSuccess: ({ url }, { tab }) => {
      sendToStripe(tab, url);
      if (tab && !tab.closed) {
        markCheckoutStarted(cachedCredits(queryClient));
        onStarted?.();
      }
    },
    onError: (_error, { tab }) => tab?.close(),
  });

  function startCheckout(offerId: OfferId) {
    const stripeTab = openStripeTab();
    checkout.mutate({
      offerId,
      returnPath: stripeTab
        ? CHECKOUT_CLOSE_PATH
        : window.location.pathname + window.location.search,
      tab: stripeTab,
    });
  }

  return { startCheckout, checkout };
}
